<template>
  <div class="carousel-wrapper">
    <!-- Carousel Container -->
    <div class="carousel-container" @touchstart="touchStart" @touchmove="touchMove" @touchend="touchEnd">
      <div class="carousel-track" :style="trackStyle">
        <div
          v-for="(img, index) in images"
          :key="index"
          class="carousel-slide"
          :class="{ 'active': index === currentIndex }"
          @click="goTo(index)"
        >
          <img :src="img" alt="Slide Image" />
          <div class="overlay" v-if="index !== currentIndex"></div>
        </div>
      </div>
    </div>

    <!-- Navigation Buttons -->
    <button class="nav-btn prev-btn" @click="prev">
      <i class="bi bi-chevron-left"></i>
    </button>
    <button class="nav-btn next-btn" @click="next">
      <i class="bi bi-chevron-right"></i>
    </button>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'

// Available images from public folder
// Note: Adjusted paths to match typical public folder serving (root relative)
const images = ref([
  '/inritek-web/images/1.jpg',
  '/inritek-web/images/2.png',
  '/inritek-web/images/3.png',
])

const currentIndex = ref(1) // Start at middleish if possible, or 0
const autoPlayInterval = 4000
let timer = null
let touchStartX = 0
let touchEndX = 0

const slideWidthPercent = ref(55) // Will be updated on mount

// Calculate transform to center the current index
const trackStyle = computed(() => {
  // Center Position (50%) - Half Slide (width/2) - (Index * Slide Width)
  const centerOffset = 50 - (slideWidthPercent.value / 2)
  const translateVal = centerOffset - (currentIndex.value * slideWidthPercent.value)
  return {
    transform: `translateX(${translateVal}%)`
  }
})

const updateLayout = () => {
  if (window.innerWidth <= 768) {
    slideWidthPercent.value = 85
  } else {
    slideWidthPercent.value = 55
  }
}

const next = () => {
  if (currentIndex.value < images.value.length - 1) {
    currentIndex.value++
  } else {
    currentIndex.value = 0 // Loop back to start
  }
  resetTimer()
}

const prev = () => {
  if (currentIndex.value > 0) {
    currentIndex.value--
  } else {
    currentIndex.value = images.value.length - 1 // Loop to end
  }
  resetTimer()
}

const goTo = (index) => {
  currentIndex.value = index
  resetTimer()
}

const startTimer = () => {
  timer = setInterval(next, autoPlayInterval)
}

const stopTimer = () => {
  clearInterval(timer)
}

const resetTimer = () => {
  stopTimer()
  startTimer()
}

// Touch handling for basic swipe
const touchStart = (e) => {
  touchStartX = e.changedTouches[0].screenX
  stopTimer()
}

//const touchMove = (e) => {
  // Optional: add real-time dragging effect here
//}

const touchEnd = (e) => {
  touchEndX = e.changedTouches[0].screenX
  handleSwipe()
  startTimer()
}

const handleSwipe = () => {
  if (touchEndX < touchStartX - 50) {
    next()
  }
  if (touchEndX > touchStartX + 50) {
    prev()
  }
}

onMounted(() => {
  updateLayout()
  window.addEventListener('resize', updateLayout)
  startTimer()
})

onUnmounted(() => {
  window.removeEventListener('resize', updateLayout)
  stopTimer()
})
</script>

<style scoped>
.carousel-wrapper {
  position: relative;
  width: 100%;
  overflow: hidden;
  padding: 40px 0; /* Add vertical padding */
  background-color: #f8f9fa;
}

.carousel-container {
  width: 100%;
  overflow: hidden;
}

.carousel-track {
  display: flex;
  transition: transform 0.5s cubic-bezier(0.25, 0.8, 0.25, 1);
  will-change: transform;
  /* Ensure the track is wide enough */
  width: 100%; 
}

.carousel-slide {
  flex: 0 0 55%; /* Fixed width 55% */
  max-width: 55%;
  transition: transform 0.5s ease, opacity 0.5s ease;
  padding: 0 10px; /* Gap between slides */
  box-sizing: border-box;
  position: relative;
  opacity: 0.5;
  transform: scale(0.85);
  cursor: pointer;
}

.carousel-slide.active {
  opacity: 1;
  transform: scale(1);
  z-index: 2;
}

.carousel-slide img {
  width: 100%;
  height: 600px;
  object-fit: cover;
  border-radius: 12px;
  box-shadow: 0 10px 20px rgba(0,0,0,0.1);
}

/* Optional Overlay for inactive slides to darken them */
/* .overlay {
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(255,255,255,0.5);
  border-radius: 12px;
} */

.nav-btn {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background-color: rgba(255, 255, 255, 0.8);
  border: none;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  font-size: 1.5rem;
  color: #333;
  cursor: pointer;
  z-index: 10;
  box-shadow: 0 4px 6px rgba(0,0,0,0.1);
  transition: background-color 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.nav-btn:hover {
  background-color: #fff;
}

.prev-btn {
  left: 20px;
}

.next-btn {
  right: 20px;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .carousel-slide {
    flex: 0 0 85%; /* Wider slide on mobile */
    max-width: 85%;
  }
  
  /* Recalculate center for 85%: 50% - 42.5% = 7.5% */
  /* Logic needs to be dynamic if we change CSS width. 
     For simplicity in this CSS-driven approach, we might need JS to detect width or just keep 60% on mobile but it looks small.
     Let's keep 60% for now or use media query specific styles if we moved calculation to CSS variables. 
     But JS uses `slideWidthPercent`. Let's stick to 60% for simplicity and consistency with the "sides visible" request. 
     60% on mobile allows good visibility of next/prev.
  */
  
  .carousel-slide img {
    height: 300px;
  }
}
</style>